import { lazy, Suspense, useState, type ComponentType } from "react";
import type { Area, CropperProps } from "react-easy-crop";
import { useMediaSrc } from "./media.tsx";

/** react-easy-crop is loaded with the first cropper (sticker creation), not with the app. */
const Cropper = lazy(() => import("react-easy-crop")) as unknown as ComponentType<Partial<CropperProps> & Pick<CropperProps, "crop" | "zoom" | "aspect" | "onCropChange">>;

export type CropRect = { x: number; y: number; width: number; height: number };

export type ImageCropperProps = {
  /** Media pipeline source of the picture (an object URL for a picked file). */
  src: string;
  /** Width / height of the crop (1 = square stickers). */
  aspect?: number;
  /** The crop in source pixels, reported after every drag/zoom (and once on load: the centred default). */
  onCropChange: (rect: CropRect) => void;
  /** Accessible name of the cropping area (translated). */
  label: string;
  testID: string;
};

/**
 * Drag + pinch/wheel zoom crop over a picture, fixed aspect. Web: react-easy-crop (lazy chunk). Native: a hand-written
 * crop view (primitive-gaps.md: iOS `ScrollView` + `MagnifyGesture` over the image with a square mask; Android
 * `Modifier.transformable` over a Coil image).
 */
export function ImageCropper({ src, aspect = 1, onCropChange, label, testID }: ImageCropperProps) {
  const url = useMediaSrc(src);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const report = (_area: Area, pixels: Area) =>
    onCropChange({ x: Math.round(pixels.x), y: Math.round(pixels.y), width: Math.round(pixels.width), height: Math.round(pixels.height) });
  return (
    <div className="b-cropper" data-testid={testID} role="group" aria-label={label}>
      {url === undefined ? null : (
        <Suspense fallback={null}>
          <Cropper image={url} crop={crop} zoom={zoom} aspect={aspect} onCropChange={setCrop} onZoomChange={setZoom} onCropComplete={report} showGrid={false} objectFit="contain" />
        </Suspense>
      )}
    </div>
  );
}
